import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { Slider } from '@material-ui/core';
import { Document, Page, pdfjs } from 'react-pdf';
import { usePinch } from '@use-gesture/react';
import { useTranslation } from 'react-i18next';

import styles from './index.module.css';
import x from 'common/dist/assets/x.svg';

import * as logger from '../../../logger';

const pdfjsWorker = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface Props extends Modal.Props {
  documentUrl: string;
  headerText?: string;
}

const MIN_ZOOM = 1.0;
const MAX_ZOOM = 4.0;

const DocumentDisplayModal: React.FC<Props> = ({
  documentUrl,
  headerText,
  ...props
}) => {
  useEffect(() => {
    // need to override certain gesture behavior to avoid issues w/ use-gesture
    const handler = (e: Event) => e.preventDefault();
    document.addEventListener('gesturestart', handler);
    document.addEventListener('gesturechange', handler);
    return () => {
      document.removeEventListener('gesturestart', handler);
      document.removeEventListener('gesturechange', handler);
    };
  }, []);
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [loadSuccess, setLoadSuccess] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const setZoomWithScroll = (newZoom: number, origin?: [number, number]) => {
    if (newZoom === zoom) return;
    const ref = containerRef.current;
    if (ref) {
      const scaleCorrection = newZoom / zoom - 1;
      const rect = ref.getBoundingClientRect();

      // center of the box
      let dx = rect.width / 2 - rect.left;
      let dy = rect.height / 2 - rect.top;
      if (origin) {
        // use mouse origin instead
        const [ox, oy] = origin;
        dx = ox - rect.left;
        dy = oy - rect.top;
      }

      // NOTE: There's an issue with scrolling from max fit where setting scroll before zoom doesn't work...
      ref.scrollLeft = ref.scrollLeft * (newZoom / zoom) + dx * scaleCorrection;
      ref.scrollTop = ref.scrollTop * (newZoom / zoom) + dy * scaleCorrection;
    }
    setZoom(newZoom);
  };

  usePinch(
    ({ origin: [ox, oy], offset: [scale], memo }) => {
      if (!containerRef.current) return;
      memo = scale;

      setZoomWithScroll(scale, [ox, oy]);
      return memo;
    },
    {
      target: containerRef,
      scaleBounds: { min: MIN_ZOOM, max: MAX_ZOOM },
      from: () => [zoom, 0],
    },
  );
  return (
    <Modal
      appElement={document.getElementById('root') ?? undefined}
      className={styles.ModalBody}
      overlayClassName={styles.Overlay}
      shouldCloseOnOverlayClick={true}
      {...props}
    >
      <div
        ref={headerRef}
        className={classNames(styles.ModalHeader, styles.Prompt)}
      >
        {`${headerText || documentUrl} (PDF)`}
        {props.onRequestClose && (
          <img
            className={styles.Icon}
            onClick={(event) => {
              props.onRequestClose && props.onRequestClose(event);
              setLoadSuccess(false);
              setZoom(1.0);
            }}
            alt="close"
            src={x}
          />
        )}
      </div>
      <div ref={containerRef} className={styles.ModalContent}>
        <Document
          className={styles.Document}
          renderMode="svg"
          file={documentUrl}
          onLoadSuccess={(pdf) => {
            setNumPages(pdf.numPages);
            setLoadSuccess(true);
          }}
          onLoadError={(err) => {
            logger.error(err);
          }}
          error={
            <div className={styles.LoadingText}>
              {t('Failed to load PDF file.')}
            </div>
          }
          loading={
            <div className={styles.LoadingText}>{t('Loading PDF...')}</div>
          }
        >
          {Array.from(new Array(numPages), (_, idx) => (
            <Page
              className={styles.Page}
              width={
                containerRef.current?.getBoundingClientRect().width || undefined
              }
              scale={zoom}
              key={`pdf-page-${idx}`}
              pageIndex={idx}
            />
          ))}
        </Document>
      </div>
      {loadSuccess && (
        <div className={styles.ZoomContainer}>
          <div>-</div>
          <Slider
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.25}
            value={zoom}
            defaultValue={zoom}
            className={styles.Slider}
            onChange={(e, value: number | number[]) => {
              if (!Array.isArray(value)) {
                setZoomWithScroll(value);
              }
            }}
          />
          <div>+</div>
        </div>
      )}
    </Modal>
  );
};

export default DocumentDisplayModal;
